import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Building2, Package, Megaphone, Edit, Save, Plus, Upload, Trash2, Eye, Users, TrendingUp } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';

export default function ManufacturerDashboard() {
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [manufacturerData, setManufacturerData] = useState({});
  const [newsForm, setNewsForm] = useState({ title: '', content: '', image_url: '' });
  const [claimRequest, setClaimRequest] = useState('');
  const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: manufacturer } = useQuery({
    queryKey: ['my-manufacturer'],
    queryFn: async () => {
      const result = await base44.entities.Manufacturer.filter({ claimed_by_user_id: user?.id });
      return result[0];
    },
    enabled: !!user?.id,
  });

  const { data: products = [] } = useQuery({
    queryKey: ['manufacturer-products', manufacturer?.brand],
    queryFn: () => base44.entities.Product.filter({ brand: manufacturer?.brand }),
    enabled: !!manufacturer?.brand,
  });

  const { data: news = [] } = useQuery({
    queryKey: ['my-manufacturer-news', manufacturer?.id],
    queryFn: () => base44.entities.ManufacturerNews.filter({ manufacturer_id: manufacturer?.id }),
    enabled: !!manufacturer?.id,
  });

  const { data: allManufacturers = [] } = useQuery({
    queryKey: ['all-manufacturers'],
    queryFn: () => base44.entities.Manufacturer.list(),
  });

  const { data: productActivities = [] } = useQuery({
    queryKey: ['product-activities', manufacturer?.brand],
    queryFn: async () => {
      if (!manufacturer?.brand) return [];
      const activities = await base44.entities.ProductActivity.list('-created_date', 50);
      const manufacturerProducts = products.filter(p => p.brand === manufacturer.brand);
      const productIds = manufacturerProducts.map(p => p.id);
      return activities.filter(a => productIds.includes(a.product_id));
    },
    enabled: !!manufacturer?.brand && !!products.length,
  });

  // Get unique users who scanned products
  const uniqueUsers = React.useMemo(() => {
    const userIds = new Set(productActivities.filter(a => a.user_id).map(a => a.user_id));
    return userIds.size;
  }, [productActivities]);

  const updateManufacturerMutation = useMutation({
    mutationFn: (data) => base44.entities.Manufacturer.update(manufacturer.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-manufacturer'] });
      setEditMode(false);
      toast.success('Perfil actualizado');
    },
  });

  const createNewsMutation = useMutation({
    mutationFn: (data) => base44.entities.ManufacturerNews.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-manufacturer-news'] });
      setNewsForm({ title: '', content: '', image_url: '' });
      toast.success('Comunicado creado');
    },
  });

  const updateNewsMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.ManufacturerNews.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-manufacturer-news'] });
      toast.success('Comunicado actualizado');
    },
  });

  const deleteNewsMutation = useMutation({
    mutationFn: (id) => base44.entities.ManufacturerNews.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-manufacturer-news'] });
      toast.success('Comunicado eliminado');
    },
  });

  const claimManufacturerMutation = useMutation({
    mutationFn: async (manufacturerId) => {
      return base44.entities.Manufacturer.update(manufacturerId, {
        claimed_by_user_id: user.id
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-manufacturer'] });
      toast.success('Perfil reclamado exitosamente');
    },
  });

  const handleSaveProfile = () => {
    updateManufacturerMutation.mutate(manufacturerData);
  };

  const handleCreateNews = () => {
    if (!newsForm.title || !newsForm.content) {
      toast.error('Completa todos los campos');
      return;
    }
    createNewsMutation.mutate({
      ...newsForm,
      manufacturer_id: manufacturer.id,
      published: false
    });
  };

  const handleImageUpload = async (file, field) => {
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      if (field === 'news') {
        setNewsForm({ ...newsForm, image_url: file_url });
      } else {
        setManufacturerData({ ...manufacturerData, [field]: file_url });
      }
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error al subir imagen');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400">Cargando...</p>
      </div>
    );
  }

  if (!manufacturer) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 pb-20">
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 text-lg">Panel de Fabricante</h1>
                <p className="text-xs text-gray-400">Gestiona tu perfil y productos</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Reclamar Perfil de Fabricante</h2>
            <p className="text-gray-600 mb-6">
              Selecciona el fabricante que representas para gestionar su perfil
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {allManufacturers.filter(m => !m.claimed_by_user_id).map((m) => (
                <Card key={m.id} className="p-4 text-left hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-1">{m.name}</h3>
                  {m.brand && <p className="text-sm text-gray-500 mb-3">{m.brand}</p>}
                  <Button
                    onClick={() => claimManufacturerMutation.mutate(m.id)}
                    size="sm"
                    className="w-full"
                  >
                    Reclamar perfil
                  </Button>
                </Card>
              ))}
            </div>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 pb-20">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 text-lg">Panel de Fabricante</h1>
                <p className="text-xs text-gray-400">{manufacturer.name}</p>
              </div>
            </div>
            <Link to={createPageUrl('Manufacturer') + `?id=${manufacturer.id}`}>
              <Button variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                Ver perfil público
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="profile">Perfil</TabsTrigger>
            <TabsTrigger value="products">Productos ({products.length})</TabsTrigger>
            <TabsTrigger value="activity">Actividad</TabsTrigger>
            <TabsTrigger value="news">Comunicados ({news.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-semibold text-gray-900">Información del Perfil</h2>
                {!editMode ? (
                  <Button onClick={() => {
                    setEditMode(true);
                    setManufacturerData(manufacturer);
                  }}>
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditMode(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSaveProfile}>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar
                    </Button>
                  </div>
                )}
              </div>

              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Nombre</label>
                    <Input
                      value={manufacturerData.name || ''}
                      onChange={(e) => setManufacturerData({ ...manufacturerData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Marca</label>
                    <Input
                      value={manufacturerData.brand || ''}
                      onChange={(e) => setManufacturerData({ ...manufacturerData, brand: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Descripción</label>
                    <Textarea
                      value={manufacturerData.description || ''}
                      onChange={(e) => setManufacturerData({ ...manufacturerData, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Historia</label>
                    <Textarea
                      value={manufacturerData.history || ''}
                      onChange={(e) => setManufacturerData({ ...manufacturerData, history: e.target.value })}
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Política de Calidad</label>
                    <Textarea
                      value={manufacturerData.quality_policy || ''}
                      onChange={(e) => setManufacturerData({ ...manufacturerData, quality_policy: e.target.value })}
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Sitio Web</label>
                    <Input
                      value={manufacturerData.website || ''}
                      onChange={(e) => setManufacturerData({ ...manufacturerData, website: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Email de Contacto</label>
                    <Input
                      type="email"
                      value={manufacturerData.contact_email || ''}
                      onChange={(e) => setManufacturerData({ ...manufacturerData, contact_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'logo_url')}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Nombre</p>
                    <p className="font-medium text-gray-900">{manufacturer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Marca</p>
                    <p className="font-medium text-gray-900">{manufacturer.brand || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Descripción</p>
                    <p className="text-gray-700">{manufacturer.description || '-'}</p>
                  </div>
                  {manufacturer.history && (
                    <div>
                      <p className="text-sm text-gray-500">Historia</p>
                      <p className="text-gray-700">{manufacturer.history}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="products">
            {products.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">No hay productos asociados</h3>
                <p className="text-gray-600 text-sm">
                  Los productos se vincularán automáticamente cuando los usuarios escaneen etiquetas con tu marca
                </p>
              </Card>
            ) : (
              <Card className="p-6">
                <h2 className="font-semibold text-gray-900 mb-4">Productos Asociados</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {products.map((product) => (
                    <div key={product.id} className="flex gap-3 p-4 bg-gray-50 rounded-lg hover:shadow-md transition-shadow">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-20 h-20 rounded-lg object-cover" />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center">
                          <Package className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">{product.name}</h3>
                        {product.category && (
                          <p className="text-xs text-gray-500 mb-2">{product.category}</p>
                        )}
                        <div className="flex items-center gap-2">
                          {product.scaneat_score !== undefined && (
                            <div className={`
                              px-2 py-1 rounded-lg text-xs font-bold
                              ${product.traffic_light === 'green' ? 'bg-green-100 text-green-700' :
                                product.traffic_light === 'yellow' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'}
                            `}>
                              {product.scaneat_score}
                            </div>
                          )}
                          <span className="text-xs text-gray-400">
                            {new Date(product.created_date).toLocaleDateString('es-AR', { 
                              day: 'numeric', 
                              month: 'short' 
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="activity" className="space-y-4">
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4 bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4" />
                  <p className="text-sm opacity-90">Usuarios Únicos</p>
                </div>
                <p className="text-3xl font-bold">{uniqueUsers}</p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <p className="text-sm opacity-90 mb-1">Total Escaneos</p>
                <p className="text-3xl font-bold">
                  {productActivities.filter(a => a.activity_type === 'scan').length}
                </p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-purple-500 to-pink-600 text-white">
                <p className="text-sm opacity-90 mb-1">Total Reseñas</p>
                <p className="text-3xl font-bold">
                  {productActivities.filter(a => a.activity_type === 'review').length}
                </p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                <p className="text-sm opacity-90 mb-1">Total Compartidos</p>
                <p className="text-3xl font-bold">
                  {productActivities.filter(a => a.activity_type === 'share').length}
                </p>
              </Card>
            </div>

            <Card className="p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Actividad Reciente</h2>
              {productActivities.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay actividad registrada aún</p>
                  <p className="text-sm text-gray-400 mt-1">
                    La actividad aparecerá cuando los usuarios interactúen con tus productos
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {productActivities.map((activity) => {
                    const product = products.find(p => p.id === activity.product_id);
                    const activityIcons = {
                      scan: '🔍',
                      review: '⭐',
                      share: '🔗',
                      view: '👁️'
                    };
                    const activityLabels = {
                      scan: 'Escaneó',
                      review: 'Reseñó',
                      share: 'Compartió',
                      view: 'Vio'
                    };
                    const activityColors = {
                      scan: 'bg-blue-50 border-blue-200',
                      review: 'bg-purple-50 border-purple-200',
                      share: 'bg-green-50 border-green-200',
                      view: 'bg-gray-50 border-gray-200'
                    };
                    
                    return (
                      <div 
                        key={activity.id} 
                        className={`flex items-center gap-3 p-4 rounded-lg border ${activityColors[activity.activity_type] || 'bg-gray-50 border-gray-200'}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-2xl">
                          {activityIcons[activity.activity_type]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            Un usuario <span className="text-blue-600">{activityLabels[activity.activity_type]}</span> tu producto
                          </p>
                          {product && (
                            <p className="text-sm text-gray-600 mt-0.5 truncate">{product.name}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(activity.created_date).toLocaleDateString('es-AR', { 
                              day: 'numeric', 
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="news" className="space-y-4">
            <Card className="p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Crear Comunicado</h2>
              <div className="space-y-4">
                <Input
                  placeholder="Título del comunicado"
                  value={newsForm.title}
                  onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                />
                <Textarea
                  placeholder="Contenido del comunicado"
                  value={newsForm.content}
                  onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
                  rows={4}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'news')}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <Button onClick={handleCreateNews}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Comunicado
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Comunicados</h2>
              <div className="space-y-3">
                {news.map((item) => (
                  <div key={item.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{item.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{item.content}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={item.published ? "outline" : "default"}
                          onClick={() => updateNewsMutation.mutate({ 
                            id: item.id, 
                            data: { published: !item.published } 
                          })}
                        >
                          {item.published ? 'Despublicar' : 'Publicar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteNewsMutation.mutate(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}